import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { RequestStatus } from '../../../../../../common/enums/requestStatus';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { RequestReferralReadButtons } from '../../../../elements/carePathButtons/RequestReferralReadButtons';
import { UserTextParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { PatientContactRead } from '../../../../elements/PatientContactRead';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ReloadEpisode } from '../../../episodes/reloadEpisode';
import { RequestMessageContainer } from '../../../episodes/RequestMessageContainer';
import { ProfessionalSummaryDataRead } from '../../../professionalSummaryData/ProfessionalSummaryDataRead';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { MedicationsRead } from '../../utils/v1/medications/MedicationsRead';
import { ProfileImages } from '../../utils/v1/ProfileImages';
import { RequestThumbnails } from '../../utils/v1/RequestThumbnails';
import { SendRequestMessage } from '../../utils/v1/SendRequestMessage';
import { ServiceConsumerSupervisorRead } from '../../utils/v1/ServiceConsumerSupervisorRead';
import { TxcsMedicationsList } from '../../utils/v1/TxcsMedicationsList';

export function GgzvV1RequestRead(props: ICustomControlRequestReadProps) {
    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => {
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || !!props.itemState.initialIsOpen,
                    });
                }}
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <ProfileImages
                    request={props.request}
                    showAlsoWhenNotRequested
                    showFormDivider
                />
                <ServiceConsumerSupervisorRead {...props} />

                <RequestThumbnails
                    showFormDivider
                    {...props}
                />

                <MedicationsRead
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    patientUsesMedication={props.request.patientUsesMedication}
                />
                <ProfessionalSummaryDataRead request={props.request} />
                <TxcsMedicationsList requestId={props.request.id} />

                {getReadCustomControlData(props.request.customControlData.echelon, 'Echelon')}
                {getReadCustomControlData(props.request.customControlData.klacht_of_hulpvraag, 'Klacht of hulpvraag')}
                {getReadCustomControlData(
                    props.request.customControlData.vermoeden_van_dsm_stoornis,
                    'Vermoeden van DSM stoornis'
                )}
                {getReadCustomControlData(props.request.customControlData.huidig_beleid, 'Huidig beleid')}
                {getReadCustomControlData(
                    props.request.customControlData.besproken_met_patient,
                    'Besproken met patiënt'
                )}
                {getReadCustomControlData(props.request.customControlData.procedurevoorstel, 'Procedurevoorstel')}

                {getReadCustomControlData(props.request.customControlData.anamnese, 'Anamnese')}
                {getReadCustomControlData(props.request.customControlData.context, 'Context')}
                {getReadCustomControlData(
                    props.request.customControlData.relevante_comorbiditeit,
                    'Relevante comorbiditeit'
                )}
                {getReadCustomControlData(
                    props.request.customControlData.suicidaliteit_en_psychose,
                    'Suïcidaliteit en psychose'
                )}
                {getReadCustomControlData(props.request.customControlData.risicovol_gedrag, 'Risicovol gedrag')}
                {getReadCustomControlData(props.request.customControlData.ook_bekend_bij, 'Ook bekend bij')}

                {props.process.requestMessages && props.process.requestMessages.length > 0 && (
                    <RequestMessageContainer requestMessages={props.process.requestMessages} />
                )}

                <PatientContactRead {...props} />

                {props.request.status === RequestStatus.accepted && (
                    <SendRequestMessage
                        isLastItem={props.isLastItem}
                        reloadAll={() => props.reloadProcess(ReloadEpisode.reloadAll)}
                        request={props.request}
                    />
                )}

                <RequestReferralReadButtons
                    {...props}
                    showConfirmationMessage
                />
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('request.hideRequest')}
                    labelMore={translateKey('request.showRequest')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );
}

function getReadCustomControlData(value: string | null, label: string) {
    if (value) {
        return (
            <HorizontalFormGroupRead>
                <FormLabel>{label}</FormLabel>
                <InputContainer>
                    <UserTextParagraph>{value}</UserTextParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>
        );
    } else {
        return null;
    }
}
